import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { publicIssueApi } from '../services/publicIssueApi';
import {
    financialInstitutionOptions,
    getFinancialInstitutionName,
} from '../constants/financialInstitutions';
import './VirtualAccountIssue.css';

function VirtualAccountIssue() {
    const navigate = useNavigate();
    const location = useLocation();
    const [step, setStep] = useState(1); // 1: 본인인증, 2: 계좌인증, 3: 발급완료
    const [subStep, setSubStep] = useState(1); // 2단계 내에서: 1: 정보입력, 2: 인증번호입력
    const [loading, setLoading] = useState(false);
    const [timeLeft, setTimeLeft] = useState(300); // 5분 = 300초
    const [issueTimestamp, setIssueTimestamp] = useState(null); // 계좌 발급 시점의 타임스탬프
    const [showToast, setShowToast] = useState(false); // 복사 토스트 알림
    const [showSmsModal, setShowSmsModal] = useState(false); // 문자 발송 모달
    const [showSmsToast, setShowSmsToast] = useState(false); // 문자 발송 토스트 알림
    const [apiError, setApiError] = useState('');
    const [sessionId, setSessionId] = useState('');
    const [devVerificationCode, setDevVerificationCode] = useState('');
    const passPopupRef = useRef(null);
    const passPollIntervalRef = useRef(null);

    const isMockMode = () => {
        if (process.env.REACT_APP_MOCK_MODE === 'true') return true;
        const params = new URLSearchParams(location.search);
        if (params.get('mock') === '1') return true;
        return false;
    };

    const getErrorMessage = (error, fallbackMessage) => {
        const responseMessage = error?.response?.data?.message;
        const responseError = error?.response?.data?.error;
        const directMessage = error?.message;

        return responseMessage || responseError || directMessage || fallbackMessage;
    };

    const applyPassProfile = (session) => {
        if (session?.pass_name) {
            setUserName(session.pass_name);
        }
        if (session?.pass_phone) {
            setPhone(session.pass_phone);
        }
    };

    const stopPassPolling = () => {
        if (passPollIntervalRef.current) {
            clearInterval(passPollIntervalRef.current);
            passPollIntervalRef.current = null;
        }
    };

    const syncPassStatus = async () => {
        if (!sessionId) {
            return false;
        }

        try {
            const session = await publicIssueApi.getSession(sessionId);
            if (['PASS_OK', 'BANK_REQ', 'BANK_OK', 'ISSUED'].includes(session?.status)) {
                applyPassProfile(session);
                stopPassPolling();
                setStep(2);
                setSubStep(1);
                setLoading(false);
                setApiError('');
                if (passPopupRef.current && !passPopupRef.current.closed) {
                    passPopupRef.current.close();
                }
                passPopupRef.current = null;
                return true;
            }
        } catch (error) {
            // PASS 표준창 완료 후 서버 상태가 반영될 때까지 재시도한다.
        }

        return false;
    };

    const startPassPolling = (popup) => {
        stopPassPolling();
        passPopupRef.current = popup;
        passPollIntervalRef.current = window.setInterval(() => {
            void syncPassStatus();
        }, 1000);
    };

    useEffect(() => () => stopPassPolling(), []);

    const banks = financialInstitutionOptions;

    // 2단계 데이터
    const [bank, setBank] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [userName, setUserName] = useState('홍길동'); // PASS에서 인증된 이름
    const [userPhone, setPhone] = useState('01012341234'); // PASS에서 인증된 이름
    const [depositorName, setDepositorName] = useState('');

    // 3단계 데이터
    const [issuedBank, setIssuedBank] = useState('케이뱅크');
    const [virtualAccountNumber, setVirtualAccountNumber] = useState('123-12452-123-01');
    const [issuedAmount, setIssuedAmount] = useState(null);

    // 계좌번호 복사 함수
    const handleCopyAccountNumber = async () => {
        try {
            await navigator.clipboard.writeText(virtualAccountNumber);
            setShowToast(true);
        } catch (err) {
            // 클립보드 API가 지원되지 않는 경우 대체 방법
            const textArea = document.createElement('textarea');
            textArea.value = virtualAccountNumber;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setShowToast(true);
            } catch (err) {
                // 복사 실패 시에도 토스트는 표시하지 않음
            }
            document.body.removeChild(textArea);
        }
    };

    // 토스트 자동 닫기 (3초 후)
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showToast]);

    // 문자 발송 토스트 자동 닫기 (3초 후)
    useEffect(() => {
        if (showSmsToast) {
            const timer = setTimeout(() => {
                setShowSmsToast(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showSmsToast]);

    // 문자 발송 확인
    const handleSendSms = () => {
        setShowSmsModal(false);
        setShowSmsToast(true);
        // 실제 문자 발송 API 호출은 여기에 구현
        console.log('계좌번호 문자 발송:', virtualAccountNumber);
    };

    const handleCloseSmsModal = () => {
        setShowSmsModal(false);
    };

    // 모바일 키보드 닫힐 때 스크롤 조정
    const handleInputBlur = () => {
        // 키보드가 닫힌 후 스크롤 위치 조정
        setTimeout(() => {
            if (window.visualViewport) {
                window.scrollTo(0, 0);
            } else {
                window.scrollTo(0, 0);
            }
        }, 300); // 키보드 애니메이션 완료 대기
    };

    const handleBack = () => {
        navigate(`/guide${location.search || ''}`);
    };

    const handleGoToGuide = () => {
        navigate(`/guide${location.search || ''}`);
    };

    // 발급 세션 시작
    useEffect(() => {
        const initSession = async () => {
            try {
                setApiError('');
                const params = new URLSearchParams(location.search);
                const passDone = params.get('pass_done') === '1';
                const resumedSessionId = params.get('session_id') || '';

                if (passDone && resumedSessionId) {
                    setSessionId(resumedSessionId);
                    try {
                        const resumedSession = await publicIssueApi.getSession(resumedSessionId);
                        applyPassProfile(resumedSession);
                    } catch (error) {
                        // 세션 상세 조회 실패 시에도 계좌인증 단계 진입은 유지한다.
                    }
                    setStep(2);
                    setSubStep(1);
                    setLoading(false);
                    return;
                }

                const tokenFromQuery = params.get('issue_token') || params.get('token') || '';
                let resolvedToken = tokenFromQuery || localStorage.getItem('public_issue_token') || '';

                if (!resolvedToken) {
                    if (isMockMode()) {
                        resolvedToken = 'mock-issue-token';
                    } else {
                        setApiError('발급 토큰(issue_token)이 없습니다. 안내 링크로 다시 접속해주세요.');
                        return;
                    }
                }

                localStorage.setItem('public_issue_token', resolvedToken);

                const session = await publicIssueApi.startSession(resolvedToken);
                setSessionId(session.session_id);
            } catch (error) {
                setApiError('발급 세션을 시작할 수 없습니다. 잠시 후 다시 시도해주세요.');
            }
        };
        initSession();
    }, [location.search]);

    const handlePhoneAuth = async () => {
        if (!sessionId) {
            setApiError('발급 세션이 준비되지 않았습니다.');
            return;
        }
        setLoading(true);
        try {
            setApiError('');
            const passStart = await publicIssueApi.startPass(sessionId);

            if (passStart?.mode === 'redirect' && passStart?.action_url && passStart?.fields) {
                const popup = window.open('', 'PCCV3Window', 'width=400,height=630,resizable=1,scrollbars=no,status=0,titlebar=0,toolbar=0,left=300,top=200');
                if (!popup) {
                    throw new Error('팝업이 차단되었습니다. 팝업 허용 후 다시 시도해주세요.');
                }

                const escapeHtml = (value) => String(value)
                    .replace(/&/g, '&amp;')
                    .replace(/"/g, '&quot;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;');

                const hiddenInputs = Object.entries(passStart.fields || {})
                    .map(([key, value]) => {
                        if (!value) {
                            return '';
                        }
                        return `<input type="hidden" name="${escapeHtml(key)}" value="${escapeHtml(value)}" />`;
                    })
                    .join('');

                const tempContainer = document.createElement('div');
                tempContainer.style.display = 'none';
                tempContainer.innerHTML = `
                    <form id="passForm" method="POST" target="PCCV3Window" action="${escapeHtml(passStart.action_url)}">
                      ${hiddenInputs}
                    </form>
                `;
                document.body.appendChild(tempContainer);

                const passForm = tempContainer.querySelector('#passForm');
                if (!passForm) {
                    document.body.removeChild(tempContainer);
                    throw new Error('PASS 인증 폼을 생성하지 못했습니다.');
                }

                passForm.submit();
                document.body.removeChild(tempContainer);
                startPassPolling(popup);
                return;
            }

            // mock 모드 fallback
            await publicIssueApi.completePass({
                sessionId,
                name: userName,
                phone: userPhone,
                passResult: 'success',
            });
            setStep(2);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            setApiError(getErrorMessage(error, 'PASS 인증 처리에 실패했습니다.'));
        }
    };

    const handleRequestAuthNumber = async () => {
        if (!sessionId) {
            setApiError('발급 세션이 준비되지 않았습니다.');
            return;
        }
        setLoading(true);
        try {
            setApiError('');
            const result = await publicIssueApi.requestAccountVerification({
                sessionId,
                bankCode: bank,
                accountNumber,
                accountHolderName: userName,
            });
            if (result?.dev_verification_code) {
                setDevVerificationCode(result.dev_verification_code);
            }
            setLoading(false);
            setSubStep(2);
        } catch (error) {
            setLoading(false);
            setApiError(getErrorMessage(error, '1원 인증 요청에 실패했습니다.'));
        }
    };

    const handleAccountAuth = async () => {
        if (!sessionId) {
            setApiError('발급 세션이 준비되지 않았습니다.');
            return;
        }
        setLoading(true);
        try {
            setApiError('');
            await publicIssueApi.confirmAccountVerification({
                sessionId,
                verificationCode: depositorName,
            });

            const issueResult = await publicIssueApi.issueVirtualAccount({
                sessionId,
                depositorName: userName,
                amount: 100,
                bankCode: bank,
            });

            setIssuedBank(issueResult?.bankName || getFinancialInstitutionName(bank) || '은행');
            setVirtualAccountNumber(issueResult?.virtualAccountNumber || '-');
            setIssuedAmount(issueResult?.amount || null);
            setLoading(false);
            setStep(3);
            setSubStep(1);
            // 발급 시점의 타임스탬프 저장 (5분 = 300초)
            const timestamp = Date.now();
            setIssueTimestamp(timestamp);
            localStorage.setItem('virtualAccountIssueTime', timestamp.toString());
        } catch (error) {
            setLoading(false);
            setApiError(getErrorMessage(error, '계좌 인증 또는 가상계좌 발급에 실패했습니다.'));
        }
    };

    // 3단계에서 타이머 카운트다운 (타임스탬프 기반)
    useEffect(() => {
        if (step === 3) {
            // localStorage에서 발급 시점 확인 (페이지 새로고침 대비)
            const savedTimestamp = localStorage.getItem('virtualAccountIssueTime');
            if (savedTimestamp && !issueTimestamp) {
                setIssueTimestamp(parseInt(savedTimestamp));
            }

            const calculateTimeLeft = () => {
                const timestamp = issueTimestamp || (savedTimestamp ? parseInt(savedTimestamp) : null);
                if (!timestamp) return 300; // 기본값 5분

                const elapsed = Math.floor((Date.now() - timestamp) / 1000); // 경과 시간 (초)
                const remaining = Math.max(0, 300 - elapsed); // 남은 시간 (최소 0)
                return remaining;
            };

            // 초기 시간 계산
            const initialTimeLeft = calculateTimeLeft();
            setTimeLeft(initialTimeLeft);

            // 1초마다 업데이트
            const timer = setInterval(() => {
                const remaining = calculateTimeLeft();
                setTimeLeft(remaining);

                if (remaining <= 0) {
                    clearInterval(timer);
                }
            }, 1000);

            return () => clearInterval(timer);
        }
    }, [step, issueTimestamp]);

    const handleChangeAccount = (e) => {
        e.preventDefault();
        // 계좌 정보 입력 화면으로 되돌리기
        setSubStep(1);
        setDepositorName(''); // 입금자명 초기화
    };

    // 단계별 헤더 제목 결정
    const getHeaderTitle = () => {
        if (step === 1) {
            return '본인명의 휴대폰 인증';
        } else if (step === 2) {
            if (subStep === 1) {
                return '본인명의 계좌 인증';
            } else if (subStep === 2) {
                return '입금된 계좌 입금자명 확인';
            }
        } else if (step === 3) {
            return '가상계좌 발급';
        }
        return '해피몰 1회용 입금가상계좌 발급'; // 기본값
    };

    return (
        <div className="issue-container">
            {/* Header Container - Header와 Progress Bar를 함께 고정 */}
            <div className="issue-header-container">
                {/* Header */}
                <div className="issue-header">
                    <button className="back-button" onClick={handleBack}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    <h1 className="issue-title">{getHeaderTitle()}</h1>
                </div>

                {/* Step Progress Bar */}
                <div className="step-progress-bar">
                    <div className={`step-progress-item ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                        <div className="step-progress-number">
                            {step > 1 ? '' : '1'}
                        </div>
                        <span className="step-progress-label">본인인증</span>
                    </div>
                    <div className={`step-progress-line ${step > 1 ? 'completed' : ''}`}></div>
                    <div className={`step-progress-item ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
                        <div className="step-progress-number">
                            {step > 2 ? '' : '2'}
                        </div>
                        <span className="step-progress-label">계좌인증</span>
                    </div>
                    <div className={`step-progress-line ${step > 2 ? 'completed' : ''}`}></div>
                    <div className={`step-progress-item ${step >= 3 ? 'active' : ''} ${step === 3 ? 'completed' : ''}`}>
                        <div className="step-progress-number">
                            {step === 3 ? '' : '3'}
                        </div>
                        <span className="step-progress-label">계좌발급</span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="issue-content">
                {apiError && (
                    <div style={{ margin: '12px 16px', color: '#b91c1c', fontSize: '14px' }}>
                        {apiError}
                    </div>
                )}
                {devVerificationCode && step === 2 && subStep === 2 && (
                    <div style={{ margin: '12px 16px', color: '#1d4ed8', fontSize: '13px' }}>
                        개발용 인증코드: {devVerificationCode}
                    </div>
                )}
                {/* STEP 01 콘텐츠 */}
                {step === 1 && (
                    <div className="step-content-wrapper">
                        <div className="step-content">
                            {/* <p className="section-title">본인명의의 휴대폰을 인증해주세요.</p> */}

                            <div className="pass-logo">
                                <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect width="100" height="100" rx="16" fill="#FF3B30" />
                                    <text x="50" y="58" fontSize="32" fontWeight="bold" fill="white" textAnchor="middle">PASS</text>
                                </svg>
                            </div>
                            <p className="guide-text">아래 버튼을 클릭하시면 본인 인증 화면으로 이동합니다.<br />PASS에서 본인 인증을 완료해 주세요.</p>
                            <button
                                className="auth-button"
                                onClick={handlePhoneAuth}
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="button-spinner">
                                        <span className="spinner"></span>
                                    </span>
                                ) : (
                                    '휴대폰 본인인증 하기'
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 02 콘텐츠 */}
                {step === 2 && subStep === 1 && (
                    <div className="step-content-wrapper" style={{ paddingTop: '0' }}>
                        <div className="step-content">
                            <div className="info-card">
                                <div className="info-row-container">
                                    <div className="info-row" style={{ width: '38%' }}>
                                        <span className="info-label">은행</span>
                                        <select
                                            value={bank}
                                            onChange={(e) => setBank(e.target.value)}
                                            className="info-select"
                                        >
                                            {banks.map((bankOption) => (
                                                <option key={bankOption.value} value={bankOption.value}>
                                                    {bankOption.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="info-row" style={{ width: '62%' }}>
                                        <span className="info-label">계좌번호</span>
                                        <input
                                            type="tel"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            value={accountNumber}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/[^0-9]/g, '');
                                                setAccountNumber(value);
                                            }}
                                            onBlur={handleInputBlur}
                                            placeholder="계좌번호를 입력하세요"
                                            className="info-input"
                                        />
                                    </div>
                                </div>
                                <div className="info-row-container">
                                    <div className="info-row" style={{ width: '38%' }}>
                                        <span className="info-label">이름</span>
                                        <input
                                            type="text"
                                            value={userName}
                                            disabled
                                            className="info-input"
                                        />
                                    </div>
                                    <div className="info-row" style={{ width: '62%' }}>
                                        <span className="info-label">휴대폰 번호</span>
                                        <input
                                            type="tel"
                                            value={userPhone}
                                            disabled
                                            className="info-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* <p className="section-title">본인명의의 계좌를 인증해주세요.</p> */}
                            <p className="guide-text">예금주 명은 본인 인증한 이름과 동일해야 합니다.<br />계좌 확인을 위해 입력한 계좌로 1원을 보내드립니다.</p>

                            <button
                                className="auth-button"
                                onClick={handleRequestAuthNumber}
                                disabled={!bank || !accountNumber || loading}
                            >
                                {loading ? (
                                    <span className="button-spinner">
                                        <span className="spinner"></span>
                                    </span>
                                ) : (
                                    '1원 인증하기'
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && subStep === 2 && (
                    <div className="step-content-wrapper" style={{ paddingTop: '0' }}>
                        <div className="step-content">
                            <div className="info-card">
                                <div className="info-row-container">
                                    <div className="info-row">
                                        <span className="info-label">입금자명</span>
                                        <input
                                            type="tel"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            value={depositorName}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/[^0-9]/g, '');
                                                setDepositorName(value);
                                            }}
                                            onBlur={handleInputBlur}
                                            placeholder="입금자명을 입력하세요"
                                            className="info-input"
                                        />
                                    </div>
                                </div>
                                <div className="info-row-container">
                                    <div className="info-row" style={{ width: '38%' }}>
                                        <span className="info-label">은행</span>
                                        <input
                                            type="text"
                                            value={getFinancialInstitutionName(bank) || '--'}
                                            disabled
                                            className="info-input"
                                        />
                                    </div>
                                    <div className="info-row" style={{ width: '62%' }}>
                                        <span className="info-label">계좌번호</span>
                                        <input
                                            type="tel"
                                            value={accountNumber}
                                            disabled
                                            className="info-input"
                                        />
                                    </div>
                                </div>
                                <div className="change-link-container" style={{ textAlign: 'right' }}>
                                    <a href="#" className="change-link" onClick={handleChangeAccount}>계좌번호 변경하기</a>
                                </div>
                            </div>

                            <img src="/img/deposite_notice.png" className="deposit-notice-image" alt="입금 안내" style={{ width: '100%', marginBottom: '24px' }} />

                            {/* <p className="section-title">본인명의의 계좌를 인증해주세요.</p> */}
                            <p className="guide-text">입력하신 계좌로 1원이 입금되었습니다.<br />입금내역에 표시된 입금자명(4자리 숫자)을 아래 입력란에 정확히 입력해 주세요.</p>

                            <button
                                className="auth-button"
                                onClick={handleAccountAuth}
                                disabled={!depositorName || loading}
                            >
                                {loading ? (
                                    <span className="button-spinner">
                                        <span className="spinner"></span>
                                    </span>
                                ) : (
                                    '계좌 인증하기'
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 03 콘텐츠 */}
                {step === 3 && (
                    <div className="step-content-wrapper">
                        <div className="step-content">
                            {/* <p className="section-title">1회용 입금가상계좌가 발급되었습니다.</p> */}

                            <div className="deposit-info deposit-info-issued">
                                {issuedAmount && (
                                    <div className="info-row" style={{ justifyContent: 'center', paddingBottom: '12px', borderBottom: '1px solid #eee', marginBottom: '12px' }}>
                                        <span style={{ fontSize: '24px', fontWeight: '700', color: '#2563eb' }}>
                                            {Number(issuedAmount).toLocaleString()}원
                                        </span>
                                    </div>
                                )}
                                <div className="info-row account-number-row">
                                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <span style={{ whiteSpace: 'nowrap' }}>{issuedBank}</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <span className="account-number-value" style={{ whiteSpace: 'nowrap' }}>{virtualAccountNumber}</span>
                                            <div style={{ display: 'flex' }}>
                                                <button className="copy-button" onClick={handleCopyAccountNumber} title="복사" style={{ padding: '5px', flexShrink: 0 }}>
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor" />
                                                    </svg>
                                                </button>
                                                <button className="sms-button" onClick={() => setShowSmsModal(true)} title="문자 발송" style={{ padding: '5px', flexShrink: 0 }}>
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H6L4 18V4H20V16ZM7 9H17V11H7V9ZM7 12H14V14H7V12Z" fill="currentColor" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={`time-warning time-warning-inline ${timeLeft === 0 ? 'expired' : ''}`}>
                                    {timeLeft > 0 ? (
                                        <p className="time-text">이 계좌는 <span className="time-count">{Math.floor(timeLeft / 60)}분 {timeLeft % 60 < 10 ? '0' : ''}{timeLeft % 60}초</span> 동안 사용 가능합니다.</p>
                                    ) : (
                                        <p className="time-text expired-text">만료시간이 다 되었습니다.</p>
                                    )}
                                </div>
                            </div>

                            <button
                                className="back-to-guide-button"
                                onClick={handleGoToGuide}
                            >
                                종료하기
                            </button>

                            {/* <p className="guide-text">가상계좌 정보를 확인하시고 입금해주세요.<br />해당 계좌는 1회성 계좌로, 사용 후 자동으로 소멸됩니다.</p> */}
                        </div>
                    </div>
                )}
            </div>

            {/* 복사 토스트 알림 */}
            {showToast && (
                <div className="copy-toast">
                    <div className="copy-toast-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="12" fill="#fff" fillOpacity="0.2" />
                            <path d="M7 12L10 15L17 8" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <span className="copy-toast-text">복사되었습니다.</span>
                </div>
            )}

            {/* 문자 발송 토스트 알림 */}
            {showSmsToast && (
                <div className="copy-toast">
                    <div className="copy-toast-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="12" fill="#fff" fillOpacity="0.2" />
                            <path d="M7 12L10 15L17 8" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <span className="copy-toast-text">계좌번호를 문자로 전송했습니다.</span>
                </div>
            )}

            {/* 문자 발송 확인 모달 */}
            {showSmsModal && (
                <>
                    <div className="modal-overlay" onClick={handleCloseSmsModal}></div>
                    <div className="modal-container">
                        <h3 className="modal-title">문자로 발송하시겠습니까?</h3>
                        <div className="modal-buttons">
                            <button className="modal-confirm-button" onClick={handleSendSms}>
                                예
                            </button>
                            <button className="modal-close-button" onClick={handleCloseSmsModal}>
                                아니오
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default VirtualAccountIssue;
